import { CommonConstants, AuthenticationConstants, EventConfigConstants, ContentDataConstants } from "../../constants";
import { AuthApiCaller } from "../../middlewares/AuthApiCaller";
import { USER_TOKEN } from "../../utils/Storage";
import store from "../../store/store";
import { NavigateFunction } from "react-router-dom";
import { setHeaderNavURL, showError } from "../commonActions";
import { Welcome } from "../../constants/PageSettings";
import { clearAllChatBotData } from "../../feature/chatbot-widget/utils/storage";
// Functions

/**
 * This function will load the list of Role.
 *
 * @param savedCallback
 * The function to be fired when the list has arrived.
 *
 * This function will be provided with the
 * actionparam of the data to be fetched.
 *
 * @param saveFailedCallback
 * The function to be fired when the fetching of
 * the data failed.
 *
 * This function will be provided with the
 * error-id for the failure.
 *
 */
let intervalID: any = 0;
let refreshInFlight: Promise<boolean> | null = null;
let wakeListenersBound = false;

// pre-FLEXS-11038 idle logout fired at lifeSpan - 2 min; kept for the feature-off path
const LEGACY_IDLE_LEAD_MS = 120000;
const LEGACY_REFRESH_LEAD_MS = 60000;

// WarningTimeout absent/0 in the API config = warning dialog off, previous silent idle logout applies
export const isWarningEnabled = (): boolean => USER_TOKEN.getWarningTimeout() > 0;

// idle clock (policy): warn at lastActivity + lifeSpan - warningTimeout, log out at lastActivity + lifeSpan
export const getIdleDeadline = (): number => {
  const lastActivityAt: number = USER_TOKEN.getLastActivityAt();
  const lifeSpan: number = USER_TOKEN.getLifeSpan();
  if (!lastActivityAt || !lifeSpan) return 0;
  return lastActivityAt + lifeSpan * 1000 - (isWarningEnabled() ? 0 : LEGACY_IDLE_LEAD_MS);
};

const getWarnMs = (): number => USER_TOKEN.getWarningTimeout() * 1000;
// 60s floor: a background tab ticks about once a minute, so a smaller lead could skip the refresh window
const getRefreshLeadMs = (): number => Math.max(getWarnMs(), LEGACY_REFRESH_LEAD_MS);

export const resetPassword = (
  _userName: string,
  savedCallback?: (data: any) => void, // @TODO - Remove Callback
  saveFailedCallback?: (errorId?: string) => void // @TODO - Remove Callback
) => {
  AuthApiCaller.resetPassword(_userName)
    .then((response) => {
      store.dispatch({
        type: AuthenticationConstants.RESET_PASSWORD,
        payload: response
      });
      savedCallback && savedCallback(response);
    })
    .catch(() => {
      saveFailedCallback && saveFailedCallback("Invalid/Empty username or password!\nPlease try again...");
    });
  return {
    type: CommonConstants.NO_CHANGE
  };
};

export const changePassword = (
  password: string,
  tfaDate: any,
  savedCallback?: (data: any) => void, // @TODO - Remove Callback
  saveFailedCallback?: (errorId?: string) => void // @TODO - Remove Callback
) => {
  AuthApiCaller.changePassword(password, tfaDate)
    .then((response) => {
      store.dispatch({
        type: AuthenticationConstants.CHANGE_PASSWORD,
        payload: response
      });
      savedCallback && savedCallback(response);
    })
    .catch(() => {
      saveFailedCallback && saveFailedCallback("Invalid/Empty username or password!\nPlease try again...");
    });
  return {
    type: CommonConstants.NO_CHANGE
  };
};

const startSessionMonitor = (): void => {
  clearInterval(intervalID);
  intervalID = setInterval(checkSession, 1000);
  bindWakeListeners();
  checkSession();
};

const configureSessionTimeOut = (lifeSpan: number) => {
  if (!lifeSpan || lifeSpan <= 0) return;
  USER_TOKEN.markActivity();
  localStorage.removeItem("sessionTimeOut");
  localStorage.setItem("logOffCheck", "true");
  startSessionMonitor();
};

// a full-page reload keeps the session alive in sessionStorage but wipes the module state
// and the interval, so monitoring has to be re-armed from the persisted values on app start
export const restoreSessionTimeOut = (): void => {
  if (intervalID) return;
  if (!sessionStorage.getItem("isSignedIn")) return;
  if (!getIdleDeadline() && !USER_TOKEN.seedIdleClockFromToken()) return;
  startSessionMonitor();
};

// two wall-clock clocks, so throttled background ticks can be late but never wrong:
//  - idle clock (what the user sees): dialog at idleDeadline - warn, countdown to idleDeadline
//    (warning off: warn = 0, so the dialog component logs out silently at the deadline)
//  - token clock (plumbing): keep the JWT alive while the user is not idle-expired
const checkSession = (): void => {
  const idleDeadline: number = getIdleDeadline();
  if (!idleDeadline || !sessionStorage.getItem("isSignedIn")) return;
  const now: number = Date.now();
  const warnMs: number = getWarnMs();
  const showTimeout: boolean = !!store.getState()?.auth?.showSessionTimeout;

  if (now >= idleDeadline - warnMs) {
    if (!showTimeout) {
      store.dispatch({
        type: AuthenticationConstants.SHOW_SESSION_TIMEOUT,
        payload: true
      });
    }
  } else if (showTimeout) {
    store.dispatch({
      type: AuthenticationConstants.SHOW_SESSION_TIMEOUT,
      payload: false
    });
  }

  const tokenExpiresAt: number = USER_TOKEN.getExpiresAt();
  if (now < idleDeadline && tokenExpiresAt && tokenExpiresAt - now <= getRefreshLeadMs() && !refreshInFlight) {
    refreshToken(true);
  }
};

const bindWakeListeners = (): void => {
  if (wakeListenersBound) return;
  wakeListenersBound = true;
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) checkSession();
  });
  window.addEventListener("focus", () => checkSession());
};

export const authenticate = async (_userName: string, _password: string, _tfaDate: any,
  saveFailedCallback?: () => void // @TODO - Remove Callback
) => {
  const response = await AuthApiCaller.login(_userName, _password, _tfaDate);
  try {
    if (response?.statusCode !== 201) {
      showError(response.errorMessage);
    } else {
      USER_TOKEN.set(response);
      store.dispatch({
        type: AuthenticationConstants.SIGN_IN,
        payload: response
      });
      store.dispatch({
        type: EventConfigConstants.SET_PAGE_SETTING,
        payload: Welcome
      });
      setHeaderNavURL(true);
      configureSessionTimeOut(response?.lifeSpan);
    }
  } catch (e) {
    console.log("exception occured ", e);
    e && saveFailedCallback && saveFailedCallback();
  }

  return {
    type: CommonConstants.NO_CHANGE
  };
};

export const onChangePassword = (result: boolean, twoFACheck: boolean) => {
  // FLEXS-14340: passwordChangeRequired is now tracked solely in sessionStorage
  // (USER_TOKEN), cleared on a successful change in the ChangePassword handler.
  // No redux copy is maintained here anymore; we only update page setting.
  if (!result && !twoFACheck) {
    store.dispatch({
      type: EventConfigConstants.SET_PAGE_SETTING,
      payload: { screen: Welcome }
    });
  }
};

export const logoff = async () => {
  console.log("logoff and clear the Cache/Session");

  try {
    const response = await AuthApiCaller.logout();
    // if (response?.statusCode === 200) {
    store.dispatch({
      type: ContentDataConstants.GET_API_LOADER,
      payload: ""
    });
    store.dispatch({
      type: AuthenticationConstants.SIGN_OUT,
      payload: response.result.redirectUrl
    });
    USER_TOKEN.remove();
    sessionStorage.removeItem("isSignedIn");
    clearInterval(intervalID);
    removeLocals();
  } catch (e) {
    store.dispatch({
      type: AuthenticationConstants.SIGN_OUT,
      payload: null
    });
    USER_TOKEN.remove();
    clearInterval(intervalID);
    removeLocals();
    // console.log("exception occured ", e);
  }
  return {
    type: CommonConstants.NO_CHANGE
  };
};

export const removeLocals = () => {
  localStorage.removeItem("isReview");
  localStorage.removeItem("hideButtons");
  localStorage.setItem("logOffCheck", "false");
  localStorage.removeItem("formStatus");
};

// single-flight: concurrent callers share one request, so the one-time-use
// refresh token is never sent twice (rotation race -> forced logout)
export const refreshToken = (skipAppLoader = false): Promise<boolean> => {
  if (refreshInFlight) return refreshInFlight;
  const { token, refreshToken: activeRefreshToken }: any = USER_TOKEN.get();
  refreshInFlight = AuthApiCaller.refreshTokenRoute(token, activeRefreshToken, skipAppLoader)
    .then((response: any) => {
      if (response && response.statusCode < 204) {
        USER_TOKEN.set(response);
        store.dispatch({
          type: AuthenticationConstants.REFRESH_TOKEN,
          payload: response
        });
        return true;
      }
      return recoverOrLogoff(activeRefreshToken);
    })
    .catch((error: any) => {
      console.log("error: ", error);
      return recoverOrLogoff(activeRefreshToken);
    })
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
};

// if another refresh already rotated the token pair in storage, the session is
// still alive and this failure was just the losing racer - do not log out
const recoverOrLogoff = (sentRefreshToken: string): boolean => {
  const { refreshToken: storedRefreshToken }: any = USER_TOKEN.get();
  if (storedRefreshToken && storedRefreshToken !== sentRefreshToken) return true;
  clearInterval(intervalID);
  logoff();
  return false;
};

export const uploadEmployeeDocuments = (
  fileData: FormData,
  savedCallback?: (data: any) => void, // @TODO - Remove Callback
  saveFailedCallback?: (errorId?: any) => void // @TODO - Remove Callback
) => {
  AuthApiCaller.uploadEmployeeDocuments(fileData)
    .then((response) => {
      store.dispatch({
        type: AuthenticationConstants.UPLOAD_EmployeeFILE_INFO,
        payload: response
      });
      savedCallback && savedCallback(response);
    })
    .catch((error: any) => {
      saveFailedCallback && saveFailedCallback(error.response.validationErrors.length > 0 ? error.response.validationErrors : error.message);
    });
  return {
    type: CommonConstants.NO_CHANGE
  };
};

export const getTFARegContent = (navigate?: NavigateFunction) => {
  AuthApiCaller.getTFARegContent()
    .then((response) => {
      if (response.statusCode === 200) {
        USER_TOKEN.set(response);
        store.dispatch({
          type: AuthenticationConstants.GET_TWOFA_REG_CONTENT,
          payload: response
        });
      } else {
        showError(response.errorMessage);
        clearInterval(intervalID);
        navigate?.("/logoff");
      }
    })
    .catch((error: any) => {
      console.log("exception occured ", error);
    });
  return {
    type: CommonConstants.NO_CHANGE
  };
};

export const getTFARegSelection = async (TwoFATypeID: any, isRegistration: any, navigate?: NavigateFunction) => {
  await AuthApiCaller.TwoFARegistrationSelection(isRegistration, TwoFATypeID)
    .then((response) => {
      if (response.statusCode === 200) {
        USER_TOKEN.set(response);
        store.dispatch({
          type: AuthenticationConstants.GET_REGISTRATION_FORM,
          payload: response
        });
      } else {
        showError(response?.errorMessage);
        clearInterval(intervalID);
        navigate?.("/logoff");
      }
    })
    .catch((error: any) => {
      console.log("exception occured ", error);
    });
  return {
    type: CommonConstants.NO_CHANGE
  };
};

export const sendVerificationCode = async (
  options: any,
  savedCallback?: (data: any) => void, // @TODO - Remove Callback
  saveFailedCallback?: (errorId?: string) => void // @TODO - Remove Callback
) => {
  await AuthApiCaller.sendVerificationCode(options)
    .then((response) => {
      if (response.statusCode === 200) {
        store.dispatch({
          type: AuthenticationConstants.SEND_VERIFICATION_CODE,
          payload: response
        });
        savedCallback && savedCallback(true);
      } else {
        saveFailedCallback && saveFailedCallback(response.errorMessage);
      }
    })
    .catch((error: any) => {
      console.log("exception occured ", error);
    });
  return {
    type: CommonConstants.NO_CHANGE
  };
};

export const getVerificationContent = (param: any) => {
  AuthApiCaller.getTFAVerificationContent(param)
    .then((response) => {
      if (response.statusCode === 200) {
        store.dispatch({
          type: AuthenticationConstants.GET_VERIFICATION_CONTENT,
          payload: response
        });
      } else {
        showError(response?.errorMessage);
        clearInterval(intervalID);
        logoff();
      }
    })
    .catch((error: any) => {
      console.log("exception occured ", error);
    });
  return {
    type: CommonConstants.NO_CHANGE
  };
};

export const validateTFACode = async (
  options: any,
  navigate?: NavigateFunction,
  saveFailedCallback?: (errorId?: string) => void // @TODO - Remove Callback
) => {
  try {
    const response = await AuthApiCaller.validateTFACode(options);

    if (response.statusCode === 401) {
      USER_TOKEN.remove();
      response?.errorMessage && showError(response.errorMessage)
      navigate?.("/logoff");
    } else if (response.statusCode !== 201) {
      saveFailedCallback && saveFailedCallback(response.errorMessage);
    } else if (response.statusCode === 422) {
      saveFailedCallback && saveFailedCallback(response.errorMessage);
    } else {
      USER_TOKEN.set(response);
      sessionStorage.removeItem("needTwoFARegistration");
      sessionStorage.removeItem("needTwoFAValidation");
      sessionStorage.removeItem("isRegistration");
      sessionStorage.removeItem("twoFATypeID");
      store.dispatch({
        type: AuthenticationConstants.SIGN_IN,
        payload: response
      });
      store.dispatch({
        type: EventConfigConstants.SET_PAGE_SETTING,
        payload: Welcome
      });
      setHeaderNavURL(true);

      configureSessionTimeOut(response?.lifeSpan);
      const cdt = new Date();
      const tfaDate = {
        day: cdt.getDate(),
        month: cdt.getMonth() + 1,
        year: cdt.getFullYear()
      };
      localStorage.setItem("tfaDate", JSON.stringify(tfaDate));
    }
  } catch (e) {
    console.log("exception occured ", e);
  }

  return {
    type: CommonConstants.NO_CHANGE
  };
};

export const getTwoFASettings = () => {
  AuthApiCaller.getTwoFASettings()
    .then((response) => {
      if (response.statusCode === 200) {
        store.dispatch({
          type: AuthenticationConstants.GET_TFA_SETTINGS,
          payload: response
        });
      } else {
        showError(response?.errorMessage);
      }
    })
    .catch((error: any) => {
      showError(error?.message);
    });
  return {
    type: CommonConstants.NO_CHANGE
  };
};

export const disableTwoFA = async (successCallback) => {
  try {
    const response = await AuthApiCaller.disableTwoFA();
    store.dispatch({
      type: AuthenticationConstants.TFA_DISABLE,
      payload: response
    });
    successCallback && successCallback(true);
  } catch (e) {
    console.log("exception occured ", e);
  }

  return {
    type: CommonConstants.NO_CHANGE
  };
};

export const loginSSO = (payload: any) => {
  //Test implementation for SSO validation. TODO
  try {
    if (payload.token) {
      USER_TOKEN.set(payload);
      store.dispatch({
        type: AuthenticationConstants.SIGN_IN,
        payload: payload
      });
      clearAllChatBotData();
      configureSessionTimeOut(payload?.lifeSpan);
    }
  } catch (e) {
    console.log("Error during SSO ", e);
  }
};

export const getSSOtoken = (
  req: any,
  navigate: NavigateFunction,
  savedCallback?: (data: any) => void, // @TODO - Remove Callback
  saveFailedCallback?: (errorId?: string) => void // @TODO - Remove Callback
) => {
  AuthApiCaller.getSSOtokenCaller(req)
    .then((response) => {
      if (response && response?.statusCode < 204) {
        delete response.version;
        delete response.statusCode;
        loginSSO(response);
        // getMainMenuContent(false, navigate);
        savedCallback && savedCallback(response);
      } else {
        saveFailedCallback && saveFailedCallback(response?.errorMessage);
        clearInterval(intervalID);
        navigate("/logoff");
      }
    })
    .catch((error: any) => {
      saveFailedCallback && saveFailedCallback(error?.message);
    });
  return {
    type: CommonConstants.NO_CHANGE
  };
};

export const idleTimer = (): void => {
  if (store.getState()?.auth?.showSessionTimeout) return;
  // once the warning window has begun, only the Stay button may extend - boot/background
  // calls must not dismiss the dialog or resurrect an idle-expired session after a reload
  const idleDeadline: number = getIdleDeadline();
  if (idleDeadline && Date.now() >= idleDeadline - getWarnMs()) return;
  USER_TOKEN.markActivity();
};

export const extendSession = async (): Promise<boolean> => {
  const extended: boolean = await refreshToken(true);
  if (extended) {
    USER_TOKEN.markActivity();
    store.dispatch({
      type: AuthenticationConstants.SHOW_SESSION_TIMEOUT,
      payload: false
    });
  }
  return extended;
};
